import { useEffect, useMemo, useState } from 'react';
import { Content, ContentWrapper, HeadWrapper, Side, TabWrapper, Wrapper } from './product-page.styles';
import { Head } from './head/head';
import { Article } from './article/article';
import { ProductPurchase } from '@features/product/purchase/ui/product-purchase';
import { useRouteMatch } from 'react-router-dom';
import { getQueryError } from '@shared/lib/utils/get-query-error/get-query-error';
import { useProductShortInfoSharedQuery } from '@entities/product/libs/hooks/use-product-short-info-shared-query/use-product-short-info-shared-query';
import { BannerProductInfo } from '@entities/product/ui/banner-product-info/banner-product-info';
import { Tabs } from '@vtbhome/juicyfront';
import { ITab } from '@vtbhome/juicyfront/types';
import { ProductTabs } from '../config/ui-constants';
import { ListOrdersInProduct } from '@widgets/list-orders-in-product/ui/list-orders-in-product';
import { useOrdersListSharedQuery } from '@entities/order/libs/hooks/use-orders-list-shared-query/use-orders-list-shared-query';
import { useCurrentUserSelector } from '@entities/current-user/model/selectors';
import { useUrlTabs } from '@shared/lib/hooks/use-url-tabs/use-url-tabs';
import { NetworkStateNew } from '@shared/ui/network-state-new/network-state-new';
import { UserRoles } from '@shared/model/user';
import { ModalOrderDetailedInfo } from '@widgets/modal-order-detailed-info/modal-order-detailed-info';
import { listOrdersInProductParameters } from '@shared/lib/utils/list-orders-in-product-parameters/list-orders-in-product-parameters';

export const ProductPage = () => {
    const user = useCurrentUserSelector();
    const route = useRouteMatch<{ id: string }>();
    const productId = Number(route.params.id);
    const isAdmin = user?.role.code === UserRoles.Admin;

    const [modalOrderId, setModalOrderId] = useState<number | null>(null);
    const { tabValue, handleSetTabValue } = useUrlTabs({
        searchUrlKey: 'tab',
        initialTabValue: ProductTabs.AboutProduct,
        tabValues: [ProductTabs.AboutProduct, ProductTabs.Orders],
    });

    const [getProduct, getProductResult] = useProductShortInfoSharedQuery(productId);
    const [getOrders, getOrdersResult] = useOrdersListSharedQuery(
        listOrdersInProductParameters({
            productId,
            user,
        })
    );

    const productError = getQueryError(getProductResult);
    const ordersError = getQueryError(getOrdersResult);
    const product = getProductResult.data;
    const hasOrders = Boolean(getOrdersResult.data?.items.length);
    const isLoadingProduct =
        getProductResult.isLoading || getProductResult.isFetching || getProductResult.isUninitialized;
    const isLoadingOrders = getOrdersResult.isFetching || getOrdersResult.isLoading || getOrdersResult.isUninitialized;

    const isLoadingPage = isAdmin ? isLoadingProduct : isLoadingProduct || isLoadingOrders;
    const isInitializedPage = isAdmin ? Boolean(product) : Boolean(product && getOrdersResult.data);

    const tabs: ITab[] = useMemo(() => {
        if (!product || !hasOrders || user?.role.code === UserRoles.Admin) {
            return [];
        }
        return [
            {
                label: 'О продукте',
                active: tabValue === ProductTabs.AboutProduct,
                tab: product && (
                    <TabWrapper>
                        <Article product={product} />
                    </TabWrapper>
                ),
                handler: () => {
                    handleSetTabValue(ProductTabs.AboutProduct);
                },
            },
            {
                label: 'История сделок',
                active: tabValue === ProductTabs.Orders,
                tab: (
                    <TabWrapper>
                        <ListOrdersInProduct product={product} onClickOrder={setModalOrderId} />
                    </TabWrapper>
                ),
                handler: () => {
                    handleSetTabValue(ProductTabs.Orders);
                },
            },
        ];
    }, [product, hasOrders, user?.role.code]);

    useEffect(() => {
        getProduct(productId);
    }, [productId]);

    useEffect(() => {
        if (!isAdmin) {
            getOrders(
                listOrdersInProductParameters({
                    productId,
                    user,
                })
            );
        }
    }, [productId, user]);

    return (
        <Wrapper>
            <NetworkStateNew
                isLoading={isLoadingPage}
                isInitialized={isInitializedPage}
                isError={Boolean(productError || ordersError)}
                hasData={Boolean(product?.id)}
                isFiltered={false}
                errorProps={{
                    title: `Ошибка загрузки ${productError ? 'продукта' : 'сделок'}`,
                    description: (productError || ordersError)?.message,
                }}
                emptyProps={{
                    title: 'Продукт не найден',
                }}>
                {product && (
                    <>
                        <HeadWrapper>
                            <Head product={product} />
                        </HeadWrapper>
                        <ContentWrapper>
                            <Content>
                                <BannerProductInfo product={product} />
                                {Boolean(tabs.length) && <Tabs list={tabs} />}
                                {Boolean(!tabs.length) && <Article product={product} />}
                            </Content>
                            <Side>
                                {!isAdmin && <ProductPurchase product={product} />}
                                {/* <CardOrderDetails /> */}
                            </Side>
                        </ContentWrapper>
                    </>
                )}
            </NetworkStateNew>
            <ModalOrderDetailedInfo
                isShow={Boolean(modalOrderId)}
                orderId={modalOrderId}
                onClose={() => setModalOrderId(null)}
            />
        </Wrapper>
    );
};
